import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import utils from '@ehi/global-marketing-interface/lib/utils';
import { i18n } from 'utils/i18n';
import { dtm } from 'utils/analytics';
import ANALYTICS from 'constants/analytics';
import { SCROLL } from 'constants';
import ServiceErrors from 'components/ServiceErrors';
import FlyoutTabs from 'components/FlyoutTabs';
import Button from 'components/Button';
import MessageBanner from 'components/MessageBanner';
import GenericGboCopyModal from 'components/modals/GenericGboCopyModalContent';
import RecommendedExtrasAlertModal from 'components/modals/RecommendedExtrasAlertModal';
import InPathVehicleUpgradeExtras from 'components/ReservationFlow/InPathVehicleUpgrade/InPathVehicleUpgradeExtras';
import CurrencyConversionModal from 'components/modals/CurrencyConversionModal';
import BundlesDetailsModal from 'components/modals/BundlesDetailsModal';
import ExtrasHeader from './ExtrasHeader';
import Bundles from './Bundles';
import IndividualExtras from './IndividualExtras';
import './styles/extras-select.scss';

/**
 * component for Extras Page
 *
 * @param {object} props.vehicleRate - Selected car rates
 */

const Extras = ({
  hasExtras,
  includedExtrasAndDrivers,
  showBundles,
  showRecommendedExtrasAlertModal,
  preSelectedExtras,
  openRecommendedExtrasAlertModal,
  extrasComplete,
  setAllExtras,
  vehicleRate,
  showConvertedTotalsModal,
  numberOfExtrasSelected,
  numberOfIncludedExtras,
  selectedBundle,
  genericTitle,
}) => {
  const [numberOfIndividualExtrasLoading, setNumberOfIndividualExtrasLoading] = useState(0);

  const scrollRef = useRef(null);

  const handleClickContinue = () => {
    if (showRecommendedExtrasAlertModal) {
      openRecommendedExtrasAlertModal();
    } else {
      extrasComplete();
    }
  };

  useEffect(() => {
    document.title = `${i18n('extras_main_title')} - ${genericTitle}`;
  }, []);

  useEffect(() => {
    if (utils.isArrayNotEmpty(preSelectedExtras)) {
      setAllExtras(preSelectedExtras);
    }
  }, [preSelectedExtras, setAllExtras]);

  // current place of ref it should go - heigh of progress header (this value is okay on mobile too)
  const scrollToRef = () => window.scrollTo(0, scrollRef.current.offsetTop - SCROLL.HEIGHT_OF_PROGRESS_BAR_DESKTOP);

  const toggleExtrasLoading = (extraLoadingBool) => {
    //  if numberOfIndividualExtrasLoading === 0, we know extras are not loading
    setNumberOfIndividualExtrasLoading((prev) => {
      const newExtraLoadingValue = extraLoadingBool ? prev + 1 : prev - 1;
      return newExtraLoadingValue;
    });
  };

  const areIndividualExtrasLoading = numberOfIndividualExtrasLoading !== 0;
  const displayCarClass = includedExtrasAndDrivers?.length < 4;
  const estimatedTotal = vehicleRate?.price_summary?.estimated_total_view;
  const estimatedTotalPayment = vehicleRate?.price_summary?.estimated_total_payment;
  const isForeignCurrency = estimatedTotal?.code !== estimatedTotalPayment?.code;

  const haveNoAddedExtras = numberOfExtrasSelected === 0 || numberOfExtrasSelected === numberOfIncludedExtras;
  const isSkipExtrasPillBtn = hasExtras && haveNoAddedExtras && !selectedBundle?.name;
  const buttonClass = cn(
    {
      pill: isSkipExtrasPillBtn,
    },
    'button'
  );

  const dataDtmTrack = dtm(
    ANALYTICS.UI_BUTTON,
    ANALYTICS.EXTRAS,
    isSkipExtrasPillBtn ? ANALYTICS.SKIP : ANALYTICS.CONTINUE
  );

  const i18nButtonText = isSkipExtrasPillBtn
    ? 'extras_continue_review_cta_no_extras_added'
    : 'extras_continue_review_cta_extras_selected';

  return (
    <div className='row'>
      <div className='extras-select'>
        <ExtrasHeader
          onClickContinue={handleClickContinue}
          hasExtras={hasExtras}
          individualExtraLoading={areIndividualExtrasLoading}
          scrollToRef={scrollToRef}
          isForeignCurrency={isForeignCurrency}
          showConvertedTotalsModal={showConvertedTotalsModal}
        />

        {/* Service errors related to extras selection will be shown here */}
        <ServiceErrors />

        <InPathVehicleUpgradeExtras />

        {!hasExtras ? (
          <MessageBanner
            title={i18n('extras_no_extras_available_title')}
            message={i18n('extras_no_extras_available_message')}
          />
        ) : (
          <>
            {includedExtrasAndDrivers.length > 0 && (
              <div
                className={cn('extras-select__included-section', {
                  'extras-select__included-section--no-bundles': !showBundles,
                })}
              >
                <h2 className='extras-select__included-heading'>{i18n('extras_included_in_your_reservation')}</h2>
                <FlyoutTabs
                  items={includedExtrasAndDrivers}
                  className='included-extras'
                  contentKey={'detailed_description'}
                  fullWidth
                  icon='extra'
                  hasClearBelow={displayCarClass}
                  isExtrasDrawer
                />
              </div>
            )}
            {showBundles && (
              <Bundles
                displayCarClass={displayCarClass}
                scrollToRef={scrollToRef}
                hasClearAbove={!includedExtrasAndDrivers.length}
              />
            )}
            <IndividualExtras
              shouldShowTitle={showBundles}
              toggleExtrasLoading={toggleExtrasLoading}
              scrollRef={scrollRef}
            />
            <div className='extras-footer'>
              <Button
                className={buttonClass}
                loading={areIndividualExtrasLoading}
                disabled={areIndividualExtrasLoading}
                onClick={handleClickContinue}
                data-dtm-track={dataDtmTrack}
              >
                {i18n(i18nButtonText)}
              </Button>
            </div>
          </>
        )}
        <BundlesDetailsModal />

        <GenericGboCopyModal />
        <RecommendedExtrasAlertModal />
        <CurrencyConversionModal />
      </div>
    </div>
  );
};

Extras.propTypes = {
  hasExtras: PropTypes.bool,
  includedExtrasAndDrivers: PropTypes.array,
  showBundles: PropTypes.bool,
  showRecommendedExtrasAlertModal: PropTypes.bool,
  preSelectedExtras: PropTypes.array,
  openRecommendedExtrasAlertModal: PropTypes.func,
  extrasComplete: PropTypes.func,
  setAllExtras: PropTypes.func,
  vehicleRate: PropTypes.object,
  showConvertedTotalsModal: PropTypes.func,
  numberOfExtrasSelected: PropTypes.number,
  numberOfIncludedExtras: PropTypes.number,
  selectedBundle: PropTypes.object,
  genericTitle: PropTypes.string,
};

export default Extras;
